const MAX_IMAGE_UPLOAD_BYTES = 25 * 1024 * 1024;
const MAX_IMAGE_DIMENSION = 4096;
const DEFAULT_IMAGE_QUALITY = 0.92;

const MIME_TYPE_JPEG = "image/jpeg";

function isBrowser() {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

async function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({ image, width: image.width, height: image.height });
    };
    image.onerror = (error) => {
      URL.revokeObjectURL(objectUrl);
      reject(error);
    };
    image.src = objectUrl;
  });
}

export async function prepareImageForUpload(imageFile, options = {}) {
  if (!imageFile || !isBrowser()) {
    return { file: imageFile };
  }

  const {
    sourceImage,
    maxImageDimension = MAX_IMAGE_DIMENSION,
    maxUploadBytes = MAX_IMAGE_UPLOAD_BYTES,
    imageQuality = DEFAULT_IMAGE_QUALITY,
    forceCompression = false,
    outputMimeType,
  } = options;
  let image;
  let resolvedWidth;
  let resolvedHeight;

  if (sourceImage) {
    image = sourceImage;
    resolvedWidth = sourceImage.width;
    resolvedHeight = sourceImage.height;
  } else {
    const metadata = await loadImageFromFile(imageFile);
    image = metadata.image;
    resolvedWidth = metadata.width;
    resolvedHeight = metadata.height;
  }

  const shouldCompress =
    forceCompression ||
    imageFile.size > maxUploadBytes ||
    resolvedWidth > maxImageDimension ||
    resolvedHeight > maxImageDimension;

  if (!shouldCompress) {
    return {
      file: imageFile,
      width: resolvedWidth,
      height: resolvedHeight,
    };
  }

  const scale = Math.min(1, maxImageDimension / Math.max(resolvedWidth, resolvedHeight));
  const targetWidth = Math.max(1, Math.round(resolvedWidth * scale));
  const targetHeight = Math.max(1, Math.round(resolvedHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return {
      file: imageFile,
      width: resolvedWidth,
      height: resolvedHeight,
    };
  }

  ctx.drawImage(image, 0, 0, targetWidth, targetHeight);

  const desiredMimeType = outputMimeType || imageFile.type || MIME_TYPE_JPEG;
  const blob = await new Promise((resolve) =>
    canvas.toBlob(resolve, desiredMimeType, imageQuality)
  );

  if (!blob) {
    return {
      file: imageFile,
      width: resolvedWidth,
      height: resolvedHeight,
    };
  }

  const preparedFile = new File([blob], imageFile.name || 'uploaded-image', {
    type: blob.type || desiredMimeType,
  });

  return {
    file: preparedFile,
    width: targetWidth,
    height: targetHeight,
  };
}
